import type { DashboardRevenueMonth, SalesRevenueMonth } from '@proerp/shared';

/**
 * Reine Anzeige-Helfer fürs Dashboard und die Auswertungen (testbar ohne React,
 * §10). Beträge bleiben bis zur Chart-Achse in Mikro-Einheiten als bigint (kein
 * Float, CLAUDE.md §6).
 *
 * Gerechnet wird hier nichts mehr über Listen: Kacheln, Umsatzverlauf und
 * Projektgesundheit (seit 2026-09-28/29) sowie die Analyse-Seiten holen ihre
 * Zahlen vom Server, der über ALLE Datensätze aggregiert — eine Listenseite
 * endet bei 200 bzw. 500 Zeilen. Die früheren Listen-Helfer
 * (`revenueMicrosForYear`, `monthlyRevenueSeries`, `ampelBreakdown`,
 * `criticalProjects`, …) sind deshalb entfernt.
 */

export interface MonthlyRevenuePoint {
  /** ISO-Monat (JJJJ-MM), eindeutiger Schlüssel der X-Achse. */
  month: string;
  /** Anzeige-Label, z. B. „Jan." (de-DE, kurz). */
  label: string;
  /** Netto-Umsatz in Euro — Number NUR für die Chart-Darstellung (recharts);
      die Summierung selbst läuft auf dem Server in bigint-Mikro-Einheiten (CLAUDE.md §6). */
  euros: number;
}

const MONTH_LABEL = new Intl.DateTimeFormat('de-DE', { month: 'short' });

/** Mikro-Einheiten → Euro als Number, NUR für die Chart-Achse (auf Cent abgeschnitten). */
function microsToChartEuros(micros: bigint): number {
  return Number(micros / 10_000n) / 100;
}

/**
 * Server-Monatsreihe (`GET /v1/dashboard/kpis` bzw. `…/sales-documents/summary`,
 * Monate `JJJJ-MM` in Europe/Berlin) → Chart-Punkte für `RevenueChart`.
 */
export function toMonthlyRevenuePoints(monthly: ReadonlyArray<DashboardRevenueMonth | SalesRevenueMonth>): MonthlyRevenuePoint[] {
  return monthly.map(({ month, net }) => {
    const [year, monthNumber] = month.split('-').map(Number);
    return {
      month,
      label: MONTH_LABEL.format(new Date(year, monthNumber - 1, 1)),
      euros: microsToChartEuros(BigInt(net.amount)),
    };
  });
}

/**
 * Veränderung ggü. Vorjahr in ganzen Prozent (abgeschnitten, bigint-Arithmetik);
 * `null`, wenn es im Vorjahr keinen (positiven) Umsatz gab.
 */
export function yearOverYearPercent(current: bigint, previous: bigint): number | null {
  return previous > 0n ? Number(((current - previous) * 100n) / previous) : null;
}
